import React, { createContext, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATH_INFO } from "../pages/utils/helper";
import { clearCsrfToken, clearUnauthorizedHandler, clientFetch, initCsrfToken, setUnauthorizedHandler } from "../utils/clientFetch";

export const AuthContext = createContext<{
  isAuthenticated: boolean | undefined
}>({
  isAuthenticated: undefined
})

export const UpdateAuthContext = createContext<{
  login: (value: { email: string, password: string }) => Promise<{ ok: boolean, errorMessage?: string }>
  logout: () => Promise<void>
}>({
  login: async (value: { email: string, password: string }) => ({ ok: false }),
  logout: async () => { }
})

export function useAuthContext() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined)
  const navigate = useNavigate()
  const HTTP_STATUS = {
    unauthorized: 401,
    forbidden: 403
  }
  const ABORT_ERROR_NAME = "AbortError"
  const CSRF_TOKEN_PATH = "/apis/client/csrf-token"
  const ERROR_MESSAGES = {
    invalidCredentials: 'メールアドレスまたはパスワードが違います。',
    loginFailed: 'ログインに失敗しました。'
  }

  useEffect(() => {
    const controller = new AbortController()
    const fetchAuthState = async () => {
      try {
        const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/user`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          handleUnauthorized: false
        })
        setIsAuthenticated(res.ok)
      } catch (error) {
        const errorName = error instanceof Error ? error.name : ''
        if (errorName === ABORT_ERROR_NAME) {
          return
        }
        console.error('認証状態の取得に失敗しました。', error)
        setIsAuthenticated(false)
      }
    }

    const initialize = async () => {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT
      if (!apiEndpoint) {
        console.error("APIエンドポイントが未設定です。")
        setIsAuthenticated(false)
        return
      }

      try {
        await initCsrfToken(`${apiEndpoint}${CSRF_TOKEN_PATH}`, controller.signal)
      } catch (error) {
        const errorName = error instanceof Error ? error.name : ""
        if (errorName === ABORT_ERROR_NAME) {
          return
        }
        console.error("CSRFトークン初期化に失敗しました。", error)
      }

      await fetchAuthState()
    }

    initialize()
    return () => {
      controller.abort()
    }
  }, [])

  const login = useCallback(async (value: { email: string, password: string }) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT
      if (!apiEndpoint) {
        console.error("APIエンドポイントが未設定です。")
        setIsAuthenticated(false)
        return { ok: false, errorMessage: ERROR_MESSAGES.loginFailed }
      }

      try {
        await initCsrfToken(`${apiEndpoint}${CSRF_TOKEN_PATH}`)
      } catch (error) {
        console.error("CSRFトークン初期化に失敗しました。", error)
        setIsAuthenticated(false)
        return { ok: false, errorMessage: ERROR_MESSAGES.loginFailed }
      }

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/login`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(value),
        handleUnauthorized: false
      })

      if (!res.ok) {
        if (res.status === HTTP_STATUS.unauthorized) {
          setIsAuthenticated(false)
          return { ok: false, errorMessage: ERROR_MESSAGES.invalidCredentials }
        }
        if (res.status === HTTP_STATUS.forbidden) {
          setIsAuthenticated(false)
          return { ok: false, errorMessage: ERROR_MESSAGES.loginFailed }
        }
        setIsAuthenticated(false)
        return { ok: false, errorMessage: ERROR_MESSAGES.loginFailed }
      }

      setIsAuthenticated(true)
      return { ok: true }
    } catch {
      console.error('ログイン失敗')
      setIsAuthenticated(false)
      return { ok: false, errorMessage: ERROR_MESSAGES.loginFailed }
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/logout`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: false
      })
    } catch (error) {
      console.error('ログアウト処理に失敗しました。', error)
    } finally {
      clearCsrfToken()
      setIsAuthenticated(false)
      navigate(PATH_INFO.LOGIN.PATH)
    }
  }, [navigate])

  return {
    isAuthenticated,
    login,
    logout
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, login, logout } = useAuthContext()
  const navigate = useNavigate()

  useLayoutEffect(() => {
    const onUnauthorized = () => {
      clearCsrfToken()
      navigate(PATH_INFO.LOGIN.PATH)
    }
    setUnauthorizedHandler(onUnauthorized)

    return () => {
      clearUnauthorizedHandler()
    }
  }, [navigate])

  return (
    <UpdateAuthContext.Provider value={{ login, logout }}>
      <AuthContext.Provider value={{ isAuthenticated }}>
        {children}
      </AuthContext.Provider>
    </UpdateAuthContext.Provider>
  )
}
